import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import {
  Terminal, Copy, Check, ChevronRight,
  Bot, Shield, Wifi, Key, AlertTriangle, Zap, Sparkles,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { LoginArea } from '@/components/auth/LoginArea';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAppContext } from '@/hooks/useAppContext';

// ─── Config generators ───────────────────────────────────────────────────────

function buildJsonConfig(npub: string, hexPubkey: string, relays: string[]) {
  return JSON.stringify(
    {
      skillforge: {
        anchor_npub: npub,
        anchor_pubkey: hexPubkey,
        relays,
        trusted_scorer: null,
        wot: {
          min_publisher_rank: 40,
          weight_direct_follow: 1.0,
          weight_follow_of_follow: 0.5,
          weight_unknown: 0.0,
        },
      },
    },
    null,
    2
  );
}

function buildTomlConfig(npub: string, hexPubkey: string, relays: string[]) {
  const relayLines = relays.map((r) => `  "${r}"`).join(',\n');
  return `[skillforge]
anchor_npub    = "${npub}"
anchor_pubkey  = "${hexPubkey}"
relays = [
${relayLines}
]

[skillforge.wot]
min_publisher_rank     = 40
weight_direct_follow   = 1.0
weight_follow_of_follow = 0.5
weight_unknown         = 0.0`;
}

function buildEnvConfig(npub: string, hexPubkey: string, relays: string[]) {
  return `SKILLFORGE_ANCHOR_NPUB="${npub}"
SKILLFORGE_ANCHOR_PUBKEY="${hexPubkey}"
SKILLFORGE_RELAYS="${relays.join(',')}"
SKILLFORGE_WOT_MIN_RANK=40
SKILLFORGE_WOT_WEIGHT_DIRECT=1.0
SKILLFORGE_WOT_WEIGHT_HOP2=0.5
SKILLFORGE_WOT_WEIGHT_UNKNOWN=0.0`;
}

function buildAgentPrompt(npub: string, hexPubkey: string, relays: string[], jsonConfig: string) {
  const relayList = relays.length > 0 ? relays.join(', ') : 'wss://relay.ditto.pub, wss://relay.nostr.band, wss://relay.damus.io';
  return `I want to connect my AI agent to SkillForge — a decentralized marketplace for AI agent skills and MCP servers built on Nostr.

Please set up the SkillForge integration for my agent using my Nostr identity as the Web of Trust anchor.

My details:
- Nostr npub: ${npub}
- Nostr pubkey (hex): ${hexPubkey}
- Relays: ${relayList}

Here is the complete configuration to use:

\`\`\`json
${jsonConfig}
\`\`\`

Please:
1. Save this as \`~/.openclaw/config.json\` (or the appropriate config path for my agent runtime)
2. If the file already exists, merge the \`skillforge\` key into it without overwriting other settings
3. Confirm the file was written successfully and show me the final contents

The \`anchor_npub\` and \`anchor_pubkey\` fields tell the agent to use my Nostr social graph as its trust anchor — so it only installs skills endorsed by people I follow. The \`wot\` settings control how that trust is weighted.

Once this is configured, my agent will be able to discover and install skills from SkillForge at https://skillforge.to/`;
}

// ─── Copy button ─────────────────────────────────────────────────────────────

function CopyButton({ text, className = '' }: { text: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Button
      size="sm"
      variant="ghost"
      onClick={handleCopy}
      className={`h-7 gap-1.5 text-xs ${className}`}
    >
      {copied ? (
        <><Check className="w-3.5 h-3.5 text-emerald-400" /><span className="text-emerald-400">Copied</span></>
      ) : (
        <><Copy className="w-3.5 h-3.5" /><span>Copy</span></>
      )}
    </Button>
  );
}

// ─── Code block ──────────────────────────────────────────────────────────────

function ConfigBlock({ code, lang }: { code: string; lang: string }) {
  return (
    <div className="relative rounded-xl overflow-hidden border border-white/[0.08]">
      <div className="flex items-center justify-between px-4 py-2 bg-white/[0.03] border-b border-white/[0.06]">
        <span className="text-xs text-muted-foreground font-mono">{lang}</span>
        <CopyButton text={code} />
      </div>
      <pre className="p-4 bg-white/[0.02] overflow-x-auto text-sm font-mono text-foreground/80 leading-relaxed whitespace-pre">
        <code>{code}</code>
      </pre>
    </div>
  );
}

// ─── AI Prompt block ─────────────────────────────────────────────────────────

function AgentPromptBlock({ prompt }: { prompt: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(prompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="relative rounded-xl overflow-hidden border border-white/[0.08]">
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-white/[0.03] border-b border-white/[0.06]">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-orange-400" />
          <span className="text-xs text-muted-foreground font-mono">agent-prompt.txt</span>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleCopy}
          className="h-7 gap-1.5 text-xs"
        >
          {copied ? (
            <><Check className="w-3.5 h-3.5 text-emerald-400" /><span className="text-emerald-400">Copied</span></>
          ) : (
            <><Copy className="w-3.5 h-3.5" /><span>Copy</span></>
          )}
        </Button>
      </div>
      <pre className="p-4 overflow-x-auto text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap break-words">
        {prompt}
      </pre>
    </div>
  );
}

// ─── Step pill ───────────────────────────────────────────────────────────────

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <div className="w-8 h-8 rounded-full bg-orange-500/20 border border-orange-500/30 flex items-center justify-center text-sm font-bold text-orange-400 shrink-0">
          {n}
        </div>
        <div className="w-px flex-1 bg-white/[0.06] mt-2" />
      </div>
      <div className="pb-10 flex-1 min-w-0">
        <p className="font-semibold mb-3 pt-1">{title}</p>
        {children}
      </div>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function ConnectAgent() {
  useSeoMeta({
    title: 'Connect Your Agent - SkillForge',
    description: 'Generate a ready-to-paste agent config with your Nostr identity as the WoT trust anchor.',
  });

  const { user } = useCurrentUser();
  const { config } = useAppContext();
  const [format, setFormat] = useState<'prompt' | 'json' | 'toml' | 'env'>('prompt');

  const npub = user ? nip19.npubEncode(user.pubkey) : null;
  const hexPubkey = user?.pubkey ?? null;
  const relays = config.relayMetadata.relays
    .filter((r) => r.read)
    .map((r) => r.url);

  const jsonConfig  = npub && hexPubkey ? buildJsonConfig(npub, hexPubkey, relays) : '';
  const tomlConfig  = npub && hexPubkey ? buildTomlConfig(npub, hexPubkey, relays) : '';
  const envConfig   = npub && hexPubkey ? buildEnvConfig(npub, hexPubkey, relays) : '';
  const agentPrompt = npub && hexPubkey ? buildAgentPrompt(npub, hexPubkey, relays, jsonConfig) : '';

  const activeConfig = format === 'toml' ? tomlConfig : format === 'env' ? envConfig : jsonConfig;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-2xl bg-background/60 border-b border-white/[0.06]">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500/20 to-emerald-500/20 flex items-center justify-center glow-primary-sm border border-white/[0.08] group-hover:border-white/[0.15] transition-all">
              <Terminal className="w-4 h-4 text-orange-400" />
            </div>
            <span className="font-semibold text-lg tracking-tight">SkillForge</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <Link to="/browse" className="hover:text-foreground transition-colors">Browse</Link>
            <Link to="/connect-agent" className="text-foreground font-medium">Connect Agent</Link>
            <Link to="/publish" className="hover:text-foreground transition-colors opacity-50 hover:opacity-100 text-xs">Publish</Link>
          </nav>
          <div className="flex items-center gap-3">
            <LoginArea className="max-w-60" />
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-3xl">

        {/* Page hero */}
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-xs font-medium mb-5">
            <Bot className="w-3.5 h-3.5" />
            Agent Setup
          </div>
          <h1 className="text-4xl font-bold tracking-tight mb-4">
            Connect your agent
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Your agent needs to know <em>whose</em> social graph to trust.
            Log in below and we'll generate a ready-to-paste config pre-loaded with your Nostr identity as the WoT anchor — so your agent inherits your full trust graph instantly.
          </p>
        </div>

        {/* Not logged in */}
        {!user && (
          <div className="glass-card rounded-2xl p-10 text-center mb-10">
            <div className="w-14 h-14 rounded-2xl bg-orange-500/15 flex items-center justify-center mx-auto mb-5">
              <Key className="w-7 h-7 text-orange-400" />
            </div>
            <h2 className="text-xl font-bold mb-3">Log in to generate your config</h2>
            <p className="text-muted-foreground mb-6 max-w-sm mx-auto text-sm leading-relaxed">
              We need your Nostr identity to populate the <code className="text-orange-300/90 text-xs bg-white/[0.04] px-1 py-0.5 rounded">anchor_npub</code> field.
              Nothing is stored — the config is generated entirely in your browser.
            </p>
            <LoginArea />
          </div>
        )}

        {/* Logged in — config generator */}
        {user && npub && (
          <>
            {/* Identity card */}
            <div className="glass-card rounded-2xl p-5 mb-8 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center shrink-0">
                <Shield className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium mb-0.5">Trust anchor detected</p>
                <p className="text-xs text-muted-foreground font-mono truncate">{npub}</p>
              </div>
              <CopyButton text={npub} className="shrink-0" />
            </div>

            {/* Relay summary */}
            <div className="glass rounded-xl p-4 border border-white/[0.08] mb-8">
              <div className="flex items-center gap-2 mb-3">
                <Wifi className="w-4 h-4 text-indigo-400" />
                <p className="text-sm font-medium">
                  {relays.length} read relay{relays.length !== 1 ? 's' : ''} from your NIP-65 list
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {relays.length > 0 ? relays.map((r) => (
                  <span key={r} className="text-xs font-mono bg-white/[0.04] border border-white/[0.06] px-2 py-1 rounded-lg text-muted-foreground">
                    {r.replace('wss://', '')}
                  </span>
                )) : (
                  <span className="text-xs text-muted-foreground">Using default SkillForge relays</span>
                )}
              </div>
            </div>

            {/* Format tabs */}
            <div className="mb-3 flex items-center gap-1 p-1 bg-white/[0.03] border border-white/[0.08] rounded-xl w-fit">
              {([
                { id: 'prompt', label: 'AI prompt', icon: <Sparkles className="w-3 h-3" /> },
                { id: 'json',   label: 'json',      icon: null },
                { id: 'toml',   label: 'toml',      icon: null },
                { id: 'env',    label: 'env',        icon: null },
              ] as const).map(({ id, label, icon }) => (
                <button
                  key={id}
                  onClick={() => setFormat(id)}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-mono font-medium transition-all ${
                    format === id
                      ? 'bg-gradient-to-r from-orange-500/80 to-amber-500/80 text-white shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {icon}
                  {label}
                </button>
              ))}
            </div>

            {format === 'prompt' ? (
              <>
                <AgentPromptBlock prompt={agentPrompt} />
                <p className="mt-3 text-xs text-muted-foreground">
                  Paste this into Claude, ChatGPT, Cursor, or your agent — it's pre-filled with your identity and config so the AI handles the setup for you.
                </p>
              </>
            ) : (
              <ConfigBlock
                code={activeConfig}
                lang={format === 'json' ? 'agent-config.json' : format === 'toml' ? 'agent-config.toml' : '.env'}
              />
            )}

            {/* What each field means */}
            <div className="mt-8 space-y-2">
              <p className="text-sm font-semibold mb-3">What each field does</p>
              {[
                {
                  icon: <Key className="w-3.5 h-3.5 text-orange-400" />,
                  field: 'anchor_npub / anchor_pubkey',
                  desc: 'Your Nostr identity. The agent fetches your kind:3 follow list and uses it as the root of its Web of Trust graph. Without this, WoT scoring is disabled.',
                },
                {
                  icon: <Wifi className="w-3.5 h-3.5 text-indigo-400" />,
                  field: 'relays',
                  desc: 'The Nostr relays your agent queries for skills and reviews. Taken from your NIP-65 relay list so the agent looks where you look.',
                },
                {
                  icon: <Shield className="w-3.5 h-3.5 text-emerald-400" />,
                  field: 'wot.min_publisher_rank',
                  desc: 'Minimum NIP-85 trust score (0–100) a skill publisher must have. Publishers below this threshold are rejected even if reviews look positive.',
                },
                {
                  icon: <Zap className="w-3.5 h-3.5 text-amber-400" />,
                  field: 'wot.weight_*',
                  desc: 'How much to trust a review based on the reviewer\'s distance from you. Direct follows = 1.0, follow-of-follow = 0.5, unknown = 0 (ignored entirely).',
                },
              ].map(({ icon, field, desc }) => (
                <div key={field} className="flex items-start gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                  <div className="mt-0.5 w-6 h-6 rounded-lg bg-white/[0.04] flex items-center justify-center shrink-0">{icon}</div>
                  <div>
                    <code className="text-xs text-orange-300/90">{field}</code>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* How to use — always visible */}
        <div className="mt-14">
          <h2 className="text-xl font-bold mb-8">How to use this config</h2>
          <div>
            <Step n={1} title="Log in and copy your config above">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Select your preferred format (JSON, TOML, or env vars) and hit Copy. The config is generated entirely in your browser — your keys never leave your device.
              </p>
            </Step>

            <Step n={2} title="Add it to your agent runtime">
              <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                Paste the config into your agent's configuration file or environment. For OpenClaw, place it in <code className="text-orange-300/90 text-xs bg-white/[0.04] px-1 py-0.5 rounded">~/.openclaw/config.json</code>. For other runtimes, consult their docs for where to set environment variables or config files.
              </p>
              <div className="glass rounded-xl p-3 border border-white/[0.08] text-xs font-mono text-muted-foreground">
                # OpenClaw example<br />
                openclaw config set --file ~/.openclaw/config.json
              </div>
            </Step>

            <Step n={3} title="Your agent is now WoT-aware">
              <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                From now on, when your agent discovers a skill on SkillForge it will:
              </p>
              <div className="space-y-2">
                {[
                  'Fetch your follow list (kind:3) to build the trust graph',
                  'Weight every review and vouch by the reviewer\'s graph distance from you',
                  'Reject skills whose publisher rank is below your minimum threshold',
                  'Surface only skills that people you trust have endorsed',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <ChevronRight className="w-3.5 h-3.5 text-orange-400 mt-0.5 shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
            </Step>

            <Step n={4} title="Keep it updated (optional)">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Your follow list evolves over time. Re-visit this page and regenerate the config after you follow new developers — your agent will automatically pick up the new trust graph on its next run since it fetches kind:3 live from relays.
                The config itself only needs to be updated if you switch Nostr accounts or change your relay list.
              </p>
            </Step>
          </div>
        </div>

        {/* Warning */}
        <div className="mt-4 glass rounded-2xl p-5 border border-amber-500/20">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-300 mb-1">Keep your anchor npub private-ish</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Your npub is already public on Nostr — this config contains no secrets. However, sharing your agent config publicly reveals which relays you use and your trust thresholds.
                Never put your <strong className="text-foreground">nsec</strong> (private key) anywhere near your agent config.
              </p>
            </div>
          </div>
        </div>

        {/* Learn more */}
        <div className="mt-8 flex items-center justify-between glass rounded-xl p-4 border border-white/[0.08]">
          <div>
            <p className="text-sm font-medium">Want to understand the full trust model?</p>
            <p className="text-xs text-muted-foreground mt-0.5">Read the SkillForge protocol spec including the WoT anchor design.</p>
          </div>
          <Link to="/nip#anchor">
            <Button variant="outline" size="sm" className="gap-1.5 border-white/[0.12] shrink-0">
              Read the NIP
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          </Link>
        </div>

      </main>
    </div>
  );
}
